import { gql, TypedDocumentNode } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { ConfirmDialog } from '@repo/shared-client/components/blocks'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from '@web/src/lib/intl/react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type { DeletePublicationMutation, DeletePublicationMutationVariables } from 'types/graphql'

export const DELETE_PUBLICATION: TypedDocumentNode<DeletePublicationMutation, DeletePublicationMutationVariables> = gql`
  mutation DeletePublication($publicationIdOrSlug: String!) {
    deletePublication(publicationIdOrSlug: $publicationIdOrSlug) {
      id
    }
  }
`

type CustomModalProps = {
  open: boolean
  onOpenChange?: (details: { open: boolean }) => void
}

const PublicationDelete = ({
  publication,
  ...props
}: CustomModalProps & {
  publication: {
    id: string
    name: string
  }
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [showSpinner, setShowSpinnerValue] = useState(false)

  const [deletePublication] = useMutation(DELETE_PUBLICATION, {
    update: (cache, { data }) => {
      const deletedPublicationId = data?.deletePublication?.id ?? publication.id
      const cacheId = cache.identify({ __typename: 'Publication', id: deletedPublicationId })

      if (cacheId) {
        cache.evict({ id: cacheId })
        cache.gc()
      }
    },
    onCompleted: () => {
      closeDialog()
      navigate({ to: '/publication/list' })
      toast.success(t('Publication Deleted'))
    },
    onError: (error) => {
      toast.error(error.message)
      closeDialog()
    },
  })

  const onSubmit = () => {
    setShowSpinnerValue(true)
    deletePublication({
      variables: { publicationIdOrSlug: publication.id },
    })
  }

  const closeDialog = () => {
    props?.onOpenChange?.({ open: false })
  }

  return (
    <ConfirmDialog
      confirmationPlaceholder={t('Type the publication name here to confirm')}
      confirmationPrompt={t('Please enter the publication name to confirm')}
      confirmationText={publication.name}
      loading={showSpinner}
      onConfirm={onSubmit}
      onOpenChange={(details) => {
        props?.onOpenChange?.(details)

        if (!details.open) {
          setShowSpinnerValue(false)
        }
      }}
      open={props.open}
      text={t('Deleting this publication will remove all reviews and media with it.')}
      title={t('Delete Publication')}
      variant="delete"
    />
  )
}

export default PublicationDelete
